import * as THREE from 'three/webgpu';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import type { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { AssemblyEntitySelectionHandler } from './AssemblyEntitySelectionHandler';

//------ 装配体模式下基准点线面 开始------
/**
 * 判断对象是否可作为装配体模式下基准点参考实体。
 *
 * @param object 场景对象。
 * @returns 是否允许作为装配体基准点引用。
 */
function isAssemblyReferencePointSelectableObject(object: THREE.Object3D): boolean {
    return isFeatureFace(object)
        || isFeatureLine(object)
        || isFeaturePoint(object)
        || isDatumAxis(object)
        || isDatumPoint(object)
        || isDatumPlaneObject(object);
}

/**
 * 装配体模式下基准点选择处理器。
 *
 * 职责：
 * 1. 仅在装配体基准点命令期间处理拾取与移入高亮；
 * 2. 保留 imported-model 二阶段代理拾取与 hover 代理复用；
 * 3. 与普通基准点命令分离，避免普通模式依赖装配体选择链。
 */
export class AssemblyReferencePointEntitySelectionHandler extends AssemblyEntitySelectionHandler {
    /**
     * 创建装配体模式下基准点选择处理器。
     *
     * @param entitySelectionManager 实体选择管理器。
     * @param importedModelRaycastResolver 导入模型射线解析器。
     */
    public constructor(
        entitySelectionManager: EntitySelectionManager,
        importedModelRaycastResolver: ImportedModelRaycastResolver,
    ) {
        super(entitySelectionManager, importedModelRaycastResolver, { assemblyModeEnabled: false });
    }

    /**
     * 处理鼠标按下。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标移动。
     *
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /** @inheritdoc 使用当前模式的候选规则消费最新悬停坐标。 */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        const hoveredPick = this.pickEntityAt(
            clientX,
            clientY,
            this.collectAssemblyReferencePointSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     *
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标点击。
     *
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.resolveAssemblyReferencePointSelectionPick(payload.clientX, payload.clientY);
        const picked = this.resolveClickSelectionPick(rawPicked);
        if (!picked) {
            return;
        }

        if (!isAssemblyReferencePointSelectableObject(picked.object)) {
            return;
        }

        const viewer = this.entitySelectionManager.renderer;
        const candidate = viewer.referencePointController.resolveSelectionCandidateByObjectId(picked.object.uuid);
        if (!candidate) {
            viewer.drawingNoticeCallback?.('当前实体不支持作为基准点参考', 'warning');
            return;
        }

        const accepted = viewer.referencePointController.handleSelection(candidate);
        if (!accepted) {
            viewer.drawingNoticeCallback?.('当前实体不支持作为基准点参考', 'warning');
            return;
        }

        // 成功写入后立即同步程序化高亮，保证视口与属性面板状态一致。
        viewer.referencePointController.syncSelectionVisuals();
    }

    /**
     * 收集框选候选对象。
     *
     * @returns 空候选列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return [];
    }

    /**
     * 解析当前点击应消费的装配体基准点引用命中。
     *
     * 设计说明：
     * 1. 装配体代理点线面在 hover 时已经完成二阶段命中与对象挂载；
     * 2. 点击继续复用当前 hover 代理，可以保证控制器保存的实体与 selected 高亮对象完全一致；
     * 3. 若当前 hover 不可作为装配体基准点引用，再回退到常规拾取流程。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @returns 当前点击应消费的命中结果。
     */
    private resolveAssemblyReferencePointSelectionPick(
        clientX: number,
        clientY: number,
    ): THREE.Intersection<THREE.Object3D> | null {
        const hoveredPick = this.entitySelectionManager.getHoveredPick();
        if (hoveredPick?.object && isAssemblyReferencePointSelectableObject(hoveredPick.object)) {
            return hoveredPick;
        }

        return this.pickEntityAt(
            clientX,
            clientY,
            this.collectAssemblyReferencePointSelectionTargets(),
        );
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectAssemblyReferencePointSelectionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectAssemblyReferencePointSelectionTargets();
    }

    /**
     * 收集装配体基准点模式的可拾取对象。
     *
     * @returns 可参与拾取的对象列表。
     */
    private collectAssemblyReferencePointSelectionTargets(): THREE.Object3D[] {
        return this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()).filter((candidate) => (
            isAssemblyReferencePointSelectableObject(candidate)
            || this.importedModelRaycastResolver.isImportedModelObject(candidate)
        ));
    }
}
//------ 装配体模式下基准点线面 结束------
